import { useEffect, useMemo } from 'react';

import { useEmbedBootstrap } from '../use-widget-bootstrap';
import {
  applyEmbedWidgetTheme,
  clearEmbedWidgetTheme,
  normalizeHexColor,
  parseEmbedThemeMode,
  resolveEmbedIsDark
} from '../widget-lib/widget-theme';

/**
 * Класс `dark` на <html> + семантические токены: база из `embed-ui` CSS, white-label из `widget` (GET /public/widget).
 * Без org в URL при `embed_global=1` — `primary_color` и `embed_theme` из GET /public/widget-global.
 * В контексте организации — `embed_theme` из настроек виджета (Приложение).
 */
export function EmbedWidgetTheme() {
  const { widgetConfig, guestShowcaseOrg, globalWidgetConfig } = useEmbedBootstrap();

  const themeWidget = useMemo((): Record<string, unknown> | null => {
    let orgWidgetSettings: Record<string, unknown> | undefined;

    const raw = widgetConfig?.widget;
    if (raw && typeof raw === 'object') {
      orgWidgetSettings = raw as Record<string, unknown>;
    } else {
      const guestRaw = guestShowcaseOrg?.settings?.widget;
      if (guestRaw && typeof guestRaw === 'object') {
        orgWidgetSettings = guestRaw as Record<string, unknown>;
      }
    }

    const globalColor = globalWidgetConfig ? normalizeHexColor(globalWidgetConfig.primary_color, '#3b82f6') : '#3b82f6';
    const globalTheme = globalWidgetConfig ? parseEmbedThemeMode(globalWidgetConfig.embed_theme) : 'system';

    if (orgWidgetSettings) {
      const primary = normalizeHexColor(orgWidgetSettings.primary_color || globalColor, '#3b82f6');
      const embed_theme = parseEmbedThemeMode(orgWidgetSettings.embed_theme || globalTheme);
      return { ...orgWidgetSettings, primary_color: primary, embed_theme };
    }

    if (globalWidgetConfig != null) {
      return { primary_color: globalColor, embed_theme: globalTheme };
    }
    return null;
  }, [widgetConfig, guestShowcaseOrg, globalWidgetConfig]);

  useEffect(() => {
    const root = document.documentElement;
    const w = themeWidget;
    const mode = parseEmbedThemeMode(w?.embed_theme);

    const mq = window.matchMedia('(prefers-color-scheme: dark)');

    const run = (): void => {
      const prefersDark = mq.matches;
      const isDark = resolveEmbedIsDark(mode, prefersDark);
      root.classList.toggle('dark', isDark);
      if (w) {
        applyEmbedWidgetTheme(root, w, isDark ? 'dark' : 'light');
      } else {
        clearEmbedWidgetTheme(root);
      }
    };

    run();

    if (mode === 'system') {
      mq.addEventListener('change', run);
      return () => {
        mq.removeEventListener('change', run);
        clearEmbedWidgetTheme(root);
        root.classList.remove('dark');
      };
    }
    return () => {
      clearEmbedWidgetTheme(root);
      root.classList.remove('dark');
    };
  }, [themeWidget]);

  // LAB live-preview: postMessage от WidgetEmbedLabPage с цветами для тестирования.
  useEffect(() => {
    function onMessage(e: MessageEvent): void {
      const d = e.data as { source?: string; type?: string; colors?: Record<string, unknown> } | null;
      if (!d || d.source !== 'domcrm-lab' || d.type !== 'theme_preview') return;
      if (d.colors && typeof d.colors === 'object') {
        const root = document.documentElement;
        const isDark = root.classList.contains('dark');
        applyEmbedWidgetTheme(root, d.colors, isDark ? 'dark' : 'light');
      }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  return null;
}
